import "./scheduler.css";
import React, { useState, useEffect, useCallback, useRef } from "react";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import { Button, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import CourseCard from "./dragableItem";
import { toast } from "sonner";
import CourseBubble, { checkClassroomAvailability } from "./course_bubble";
import { getAllClass, getAllClassroom } from "../../api/pedagogicElement";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import { getCoursesByAcademicYear } from "../../api/cours";
import { getColorPair } from "../../helper/randowColor";
import { getAllEDTCoursByVersion, getEDTById, getCoursesForEDTVersion } from "../../api/edt";
import EDTSaveModal from "./edt_save_modal";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import CreateCoursModal from "./cours/createCoursModal";
import { getSessionsByYear, getSettingByYear } from "../../api/settingEDT";
import { toMinutes } from "../../helper/toMinutes";
import EDTCreateModal from "./edt_create_modal";
import { useQueryState, parseAsString } from "nuqs";

import EDTApercuByClass from "./edt_apercu_modal_by_class";
import EditCoursModal from "./cours/editCoursModal";
import EDTCourseSnapshotModal from "./cours/edtCourseSnapshotModal";
import InfosAlertYearDisabled from "../../core/common/alerts/infosAlertYearDisabled";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

// Interface pour le type de données du cours
export interface CoursTable {
  id: string;
  Subject: any;
  TeacherProfil: any;
  status: string;
  attachment?: string;
  createdAt: string;
  seance_num?: number;
  id_classroom?: string | null;
  id_classes: string[];
  id_source_course?: string;
  id_versionEDT?: string | null;
}

export type Course = {
  id: string;
  label: string;
  séance_num: number;
  classeId: string[];
  courseId: string;
  classroomId?: string | null;
};

export enum CourseStatus {
  SCHEDULED = "Confirmé",
  CANCELLED = "Annulé",
}

export enum CourseState {
  NORMAL = "Normal",
  RATTRAPAGE = "Rattrapage",
}

type Classe = {
  id: string;
  name: string;
  // courses: Course[];
};

type Day = {
  id: string;
  name: string;
};

export type Slot = {
  id: string;
  dayId: string;
  seanceIndex: number;
  classeId: string;
  courseId?: string;
};

export type Seance = {
  id: string;
  index: number;
  label: string;
  start_time: string;
  end_time: string;
  duration: number;
};

const placedCourseSchema = z.object({
  id: z.string().optional(),
  slotsIds: z.array(z.string()).min(1),
  course: z.object({
    id: z.string(),
    Subject: z.any(),
    TeacherProfil: z.any(),
    status: z.string(),
    attachment: z.string().optional(),
    createdAt: z.string(),
    seance_num: z.number().optional(),
    id_classroom: z.string().optional().nullable(),
    id_classes: z.array(z.string()),
  }),
  status: z.nativeEnum(CourseStatus),
  state: z.nativeEnum(CourseState),
  reason_cancel: z.string().optional(),
  type_delay: z.string().optional(),
  classroomId: z.string().optional(),
  day_course: z.string(),
  matchedClassesCours: z.array(
    z.object({ planifiedCourse_id: z.string(), id_classe: z.string() }),
  ), // sauvegarde l'id des cours déja planifier et la classe qui correspond (uniquement utilisé pour l'ajout et la récupération côtéback)
  seance_num: z.number().int().positive().optional(),
});

const timetableSchema = z.object({
  id: z.string().optional(),
  version: z.string().optional(),
  title: z.string().optional(),
  placedCourses: z.array(placedCourseSchema),
});

type TimeTableFormData = z.infer<typeof timetableSchema>;

const Scheduler = () => {
  const { selectedYear } = useAcademicYear();

  // Get timetable ID and version from URL params
  const [timetableId, setTimeTableId] = useQueryState("id", parseAsString);
  const [timetableVersion, setTimetableVersion] = useQueryState(
    "version",
    parseAsString,
  );

  // State to store timetable data
  const [timetableData, setTimetableData] = useState<any>(null);
  const activeVersionId = timetableData?.version?.id || timetableVersion;
  const currentVersionId = timetableData?.version?.id;
  const selectedYearId = selectedYear?.id;

  const { control } = useForm<TimeTableFormData>({
    resolver: zodResolver(timetableSchema),
  });

  const { fields, replace } = useFieldArray({
    control,
    name: "placedCourses",
  });

  // Fonction pour charger les infos de l'EDT
  const fetchEDT = useCallback(async () => {
    try {
      const response = await getEDTById(timetableId);
      // console.log("response data: ", response.data);

      const formatted_timeTableData = {
        ...response.data,
        version: response.data?.VersionEDTs.find(
          (item: any) => item.id === timetableVersion,
        ),
      };

      setNewEdtName(response.data.title);
      setTimetableData(formatted_timeTableData);
    } catch (error: any) {
      // console.log("erreur de chargement des infos de l'edt : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [timetableId, timetableVersion]);

  // Fetch timetable data when ID changes
  useEffect(() => {
    if (timetableId) {
      // TODO: Implement API call to fetch timetable data
      // fetchTimetableData(timetableId, timetableVersion);
      fetchEDT();
    }
  }, [timetableId, fetchEDT]);

  // state pour le nom d'un vouvelle edt
  const [newEdtName, setNewEdtName] = useState<string>("");

  // state de la liste de tous les slots
  const [slots, setSlots] = useState<Slot[]>([]);
  const slotsRef = useRef<Slot[]>([]);

  useEffect(() => {
    slotsRef.current = slots;
  }, [slots]);

  // state pour le cours sélectioné
  const [courseSearch, setCourseSearch] = useState("");
  const [selectedClass, setSelectedClass] = useState<Classe>();
  const [openPlacedCoursePopover, setOpenPlacedCoursePopover] = useState<string | null>(null);

  const [allClassrooms, setAllClassrooms] = useState<any[]>();
  // state du cours en déplacement
  const [draggedOccurrence, setDraggedOccurence] = useState<{
    course: CoursTable;
    dayId: string;
    seanceIndex: number;
    seance_num: number;
    slotsIds: string[];
    placement: any;
  } | null>(null);

  const [roomConflictRequest, setRoomConflictRequest] = useState<{
    requestedRoomId: string;
    targetCourseId: string;
    targetSlotId: string;
    conflictingPlacement: any;
    conflictingCourseId: string;
    conflictingSlotsIds: string[];
  } | null>(null);

  const [allClass, setAllClass] = useState<{
    id: string;
    name: string;
    selectLabel: string;
  }[]>([]);
  const [allCourses, setAllCourses] = useState<CoursTable[]>([]);

  // state pour la gestion des jours ouvrables
  const [days, setDays] = useState<Day[]>([]);

  // states pour la gestion des séances
  const [seances, setSeances] = useState<Seance[]>([]);

  // state pour voir le modal de création d'un EDT
  const [showCreate, setShowCreate] = useState(false);

  // state pour voir le modal de sauvegarde
  const [showSave, setShowSave] = useState(false);

  // state pour voir le modal d'ajout de cours
  const [showCreateCourse, setShowCreateCourse] = useState(false);

  // state pour voir le modal demodification d'un cours
  const [showEditCourse, setShowEditCourse] = useState(false);

  // state du cours à modifier
  const [editCourse, setEditCourse] = useState<any>();

  // state pour voir la zone de saisie du nom de l'emploi du temps
  const [showNewEdtName, setShowNewEDTName] = useState(false);

  // state pour voir le modal d'apercu par classe
  const [showApercuByClass, setShowApercuByClass] = useState<boolean>(false);

  // Fonction pour récupérer toutes les salles de classes de l'année sélectionnée
  const fetchAllClassroom = useCallback(async () => {
    try {
      if (!selectedYear?.id) return;
      const response = await getAllClassroom(
        selectedYear?.id,
        buildPermissionContext(Resource.EDT, Action.READ),
      );
      setAllClassrooms(response.data);
    } catch (error: any) {
      // console.log("erreur de chargement des salles de classes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [selectedYear?.id]);

  // fonction pour la récupération des jours ouvrables
  const fetchWorkingDays = useCallback(async () => {
    try {
      if (!selectedYear?.id) return;
      const response = await getSettingByYear(
        selectedYear.id,
        buildPermissionContext(Resource.EDT, Action.READ),
      );

      // Convertit la chaîne JSON reçue en tableau de jours
      const rawDays: string = response.data[0].working_days; // ex: "{\"Lundi\",\"Dimanche\"}"
      const cleanedDays = rawDays.replace(/[{}]/g, ""); // retire accolades
      const daysArray = cleanedDays
        .split(",")
        .map((d) => d.replace(/"/g, "").trim()); // ["Lundi", "Dimanche"]

      // Ordre des jours de la semaine
      const weekOrder = [
        "Lundi",
        "Mardi",
        "Mercredi",
        "Jeudi",
        "Vendredi",
        "Samedi",
        "Dimanche",
      ];
      const sortedDays = daysArray.sort(
        (a, b) => weekOrder.indexOf(a) - weekOrder.indexOf(b),
      );

      const formattedDays = sortedDays.map((day: any, idx: number) => ({
        id: `d${idx + 1}`,
        name: day,
      }));

      setDays(formattedDays);
    } catch (error: any) {
      // console.log("erreur de chargement des jours ouvrables : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [selectedYear?.id]);

  // fonction pour récupérer toutes les classes
  const fetchAllClasses = useCallback(async () => {
    try {
      if (!selectedYear?.id) return;
      const response = await getAllClass(
        selectedYear?.id,
        buildPermissionContext(Resource.EDT, Action.READ),
      );
      // console.log("response classes: ", response);
      const formattedClass = response.data
        ?.slice() // pour éviter de modifier directement la réponse
        .sort((a: any, b: any) => {
          // Tri par Level.label
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;

          // Si même Level, tri par class label
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        })
        .map((item: any) => {
          const levelAbbreviation = item.Level?.abbreviation || item.Level?.label || "";
          return {
            id: item.id,
            name: item.Level?.abbreviation + " - " + item?.abbreviation,
            selectLabel: [levelAbbreviation, item.label].filter(Boolean).join(" - "),
          };
        });
      setAllClass(formattedClass || []);
    } catch (error: any) {
      // console.log("erreur de chargement des classes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [selectedYear?.id]);

  // fonction pour récupérer tous les cours
  const fetchAllCourses = useCallback(async () => {
    try {
      if (!selectedYear?.id) return;
      const permission = buildPermissionContext(Resource.EDT, Action.READ);
      const response = activeVersionId
        ? await getCoursesForEDTVersion(activeVersionId, permission)
        : await getCoursesByAcademicYear(selectedYear.id, permission);
      const formattedCourses = response.data?.map((item: any) => ({
        id: item.id,
        Subject: item.Subject,
        TeacherProfil: item.TeacherProfil,
        seance_num: item.number_session,
        id_classes: item.id_classes,
        id_classroom: item.id_classroom,
        id_source_course: item.id_source_course,
        id_versionEDT: item.id_versionEDT,
        status: item.status,
        createdAt: item.createdAt,
      }));
      setAllCourses(formattedCourses || []);
    } catch (error: any) {
      // console.log("erreur de chargement des cours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [activeVersionId, selectedYear?.id]);

  // fonction pour récupérer toutes les séances
  const fetchAllSessions = useCallback(async () => {
    try {
      if (!selectedYear?.id) return;

      const response = await getSessionsByYear(
        selectedYear?.id,
        buildPermissionContext(Resource.EDT, Action.READ),
      );
      // console.log("response data formatted: ",response.data)
      const formattedSessions = response.data.map(
        (session: any, index: number) => ({
          label: session.name,
          start_time: session.start_time?.slice(0, 5), // pour enlever les secondes
          end_time: session.end_time?.slice(0, 5),
          index: index + 1,
          id: session.id,
          duration:
            toMinutes(session.end_time?.slice(0, 5)) -
            toMinutes(session.start_time?.slice(0, 5)),
        }),
      );
      setSeances(formattedSessions);
      // console.log("Seances récupéré: ",formattedSessions)
    } catch (error: any) {
      // console.log("erreur de chargement des séances : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  }, [selectedYear?.id]);

  // récupérer tous les cours planifiée dans l'emplois du temps
  const fetchCoursePlanified = useCallback(async () => {
    try {
      if (
        !selectedYearId ||
        !currentVersionId ||
        !allCourses.length ||
        !days.length ||
        !seances.length ||
        !slots.length
      )
        return;

      const response = await getAllEDTCoursByVersion(
        currentVersionId,
        selectedYearId,
        buildPermissionContext(Resource.EDT, Action.READ),
      );

      // Récupère la liste des cours planifiés pour la version actuelle
      const versionData = response.data[0].VersionEDTs.find(
        (v: any) => v.id === currentVersionId,
      );

      if (!versionData) return;

      const courses = versionData.CourseVersionEDTs;
      // Rebuild occupancy from persisted placements so class additions/removals
      // cannot leave stale occupied cells behind in the open timetable.
      const nextSlots: Slot[] = slotsRef.current.map((slot) => ({
        ...slot,
        courseId: undefined,
      }));
      // console.log("Courses: ", courses);
      // Étape 1 : Regroupement des cours
      const grouped = courses.reduce((acc: any, course: any) => {
        const day = course.day_course;
        const sessionStart = course.id_sessionStart;
        const courseId = course.CourseClass.Course.id;

        const key = `${day}-${sessionStart}-${course.id_sessionEnd}-${courseId}`;
        if (!acc[key]) {
          acc[key] = {
            courses: [],
            classCoursPlanified: [],
          };
        }

        acc[key].courses.push(course);
        // Collect unique class-course pairs
        const classId = course.CourseClass.id_class;
        const planifiedCourseId = course.id; // ID du cours planifié
        if (classId && planifiedCourseId) {
          const pair = {
            planifiedCourse_id: planifiedCourseId,
            id_classe: classId,
          };
          // Check if the pair already exists to avoid duplicates
          const exists = acc[key].classCoursPlanified.some(
            (c: any) =>
              c.planifiedCourse_id === planifiedCourseId &&
              c.id_classe === classId,
          );
          if (!exists) {
            acc[key].classCoursPlanified.push(pair);
          }
        }

        return acc;
      }, {});

      // Étape 2 : Transformation au format placedCourseSchema
      const formattedCourses: any[] = Object.values(grouped).map(
        (group: any) => {
          // console.log("group: ", group);

          const first = group.courses[0];

          // Trouver le cours complet dans la liste allCourses (déjà chargée plus haut)
          const foundCourse = allCourses.find(
            (c) => c.id === first.CourseClass.id_course,
          );

          // On construit la liste des slotsIds
          const slotsIds =
            // foundCourse?.id_classes?.map((classId: string) => {
            group?.classCoursPlanified?.map((pair: any) => {
              const dayObj = days.find((d) => d.name === first.day_course);
              if (!dayObj) return "";
              const startSeance = seances.find(
                (s) => s.id === first.id_sessionStart,
              );
              const endSeance = seances.find(
                (s) => s.id === first.id_sessionEnd,
              );
              if (!startSeance || !endSeance) return "";
              const placementSessionCount = endSeance.index - startSeance.index + 1;
              if (placementSessionCount <= 0) return "";

              // touver les slots qui doivent être occupés
              const slotsToOccupy = nextSlots.filter(
                (s) => s.classeId === pair.id_classe && s.dayId === dayObj.id,
              );
              // occuper tous les slots qui correspondent au cours
              for (
                let index = startSeance.index - 1;
                index < startSeance.index - 1 + placementSessionCount;
                index++
              ) {
                const element = slotsToOccupy[index];
                if (element) element.courseId = foundCourse?.id;
              }
              return pair.id_classe + dayObj.id + (startSeance.index - 1);
            }) || [];

          return {
            slotsIds,
            course: foundCourse
              ? {
                  ...foundCourse,
                  id_classes: Array.from(
                    new Set<string>(group.classCoursPlanified.map((pair: any) => pair.id_classe)),
                  ),
                }
              : foundCourse,
            seance_num: (() => {
              const start = seances.find((s) => s.id === first.id_sessionStart);
              const end = seances.find((s) => s.id === first.id_sessionEnd);
              return start && end ? end.index - start.index + 1 : foundCourse?.seance_num;
            })(),
            status: first.state_course,
            state: first.type_course,
            reason_cancel: first.reason_cancel,
            classroomId: first.id_classroom,
            day_course: days.find((d) => d.name === first.day_course)?.id || "",
            matchedClassesCours: group.classCoursPlanified,
          };
        },
      );

      // console.log("formattedCourses: ", formattedCourses);

      // Étape 3 : Injecter dans le form
      setSlots(nextSlots);
      replace(formattedCourses);
      // toast.success("Cours planifiés chargés dans l'emploi du temps !");
    } catch (error: any) {
      // console.error("Erreur de chargement des cours planifiés :", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produite, réessayez plus tard",
      );
    }
  }, [
    selectedYearId,
    currentVersionId,
    allCourses,
    days,
    seances,
    slots.length,
    replace,
  ]);

  useEffect(() => {
    if (!selectedYear?.id) return;

    fetchWorkingDays();
    fetchAllSessions();
    fetchAllClasses();
    fetchAllClassroom();
  }, [selectedYear?.id, fetchWorkingDays, fetchAllSessions, fetchAllClasses, fetchAllClassroom]);

  useEffect(() => {
    if (!selectedYear?.id) return;
    fetchAllCourses();
  }, [fetchAllCourses, selectedYear?.id]);

  useEffect(() => {
    fetchCoursePlanified();
  }, [fetchCoursePlanified]);

  // initialiser les slots une seule fois
  useEffect(() => {
    if (slots.length === 0) {
      const slots = document.querySelectorAll(".slot");
      setSlots(
        Array.from(slots).map((slot) => {
          return {
            id: slot.id,
            dayId: slot.getAttribute("data-dayId") || "",
            classeId: slot.getAttribute("data-classeId") || "",
            seanceIndex: parseInt(slot.getAttribute("data-seanceIndex") || "0"),
            courseId: slot.getAttribute("data-courseId") || "",
          };
        }),
      );
    }
  }, [slots]);

  // vérifier si le classe est compatible avec le cours dropé
  const checkClasseCompatibility = (course: CoursTable, classId: string) => {
    return course.id_classes.some((id: string) => id === classId);
  };

  const getOccupiedSlotIdsForPlacement = (placement: any): string[] => {
    if (!placement?.slotsIds?.length) return [];

    const anchors = slots.filter((slot) =>
      placement.slotsIds.includes(slot.id),
    );
    const duration = placement.seance_num ?? placement.course?.seance_num ?? 1;

    return slots
      .filter((slot) =>
        anchors.some(
          (anchor) =>
            slot.classeId === anchor.classeId &&
            slot.dayId === anchor.dayId &&
            slot.seanceIndex >= anchor.seanceIndex &&
            slot.seanceIndex < anchor.seanceIndex + duration,
        ),
      )
      .map((slot) => slot.id);
  };

  const handlePlacementSessionCountChange = (
    placedCourse: any,
    anchorSlotId: string,
    requestedCount: number,
  ) => {
    if (!Number.isInteger(requestedCount) || requestedCount < 1) return;
    const anchorSlots = slots.filter((slot) =>
      placedCourse.slotsIds.includes(slot.id),
    );
    if (!anchorSlots.length) return;

    const anchorIndex = anchorSlots[0].seanceIndex;
    if (anchorSlots.some((slot) => slot.seanceIndex !== anchorIndex || slot.dayId !== placedCourse.day_course)) {
      toast.error("Les créneaux de début du cours sont incohérents.");
      return;
    }

    const courseWithRequestedDuration = {
      ...placedCourse.course,
      seance_num: requestedCount,
    };
    const teacherConflict = checkTeacherAvailability(
      courseWithRequestedDuration,
      anchorIndex,
      anchorSlots[0].dayId,
      getOccupiedSlotIdsForPlacement(placedCourse),
    );
    if (teacherConflict.isOccupied) {
      toast.error("L'enseignant est déjà occupé pendant cette plage horaire.");
      return;
    }
    if (placedCourse.classroomId) {
      const roomConflict = checkClassroomAvailability(
        slots,
        fields,
        { id: anchorSlots[0].dayId },
        courseWithRequestedDuration,
        placedCourse.classroomId,
        anchorIndex,
        anchorSlots[0].dayId,
        placedCourse.slotsIds,
      );
      if (roomConflict.isOccupied) {
        toast.error(`La salle est déjà utilisée par le cours ${roomConflict.existConflict?.course?.Subject?.label || "d'un autre enseignant"}.`);
        return;
      }
    }

    const currentSlotIds = new Set<string>();
    for (const anchor of anchorSlots) {
      const classDaySlots = slots.filter(
        (slot) => slot.classeId === anchor.classeId && slot.dayId === anchor.dayId,
      );
      if (anchorIndex + requestedCount > classDaySlots.length) {
        toast.error("Le cours dépasserait les créneaux disponibles de la classe.");
        return;
      }
      const oldCount = placedCourse.seance_num ?? placedCourse.course.seance_num ?? 1;
      classDaySlots
        .filter((slot) => slot.seanceIndex >= anchorIndex && slot.seanceIndex < anchorIndex + oldCount)
        .forEach((slot) => currentSlotIds.add(slot.id));
      const hasConflict = classDaySlots.some(
        (slot) =>
          slot.seanceIndex >= anchorIndex &&
          slot.seanceIndex < anchorIndex + requestedCount &&
          slot.courseId &&
          !currentSlotIds.has(slot.id),
      );
      if (hasConflict) {
        toast.error("Une autre séance de cette classe occupe déjà cette plage horaire.");
        return;
      }
    }

    const nextSlots = slots.map((slot) => {
      if (currentSlotIds.has(slot.id)) return { ...slot, courseId: undefined };
      const anchor = anchorSlots.find(
        (candidate) => candidate.classeId === slot.classeId && candidate.dayId === slot.dayId,
      );
      if (anchor && slot.seanceIndex >= anchorIndex && slot.seanceIndex < anchorIndex + requestedCount) {
        return { ...slot, courseId: placedCourse.course.id };
      }
      return slot;
    });
    setSlots(nextSlots);
    replace(fields.map((item: any) =>
      item.slotsIds.includes(anchorSlotId)
        ? { ...item, seance_num: requestedCount }
        : item,
    ));
  };

  const handleClassroomConflict = (
    targetCourseId: string,
    requestedRoomId: string,
    conflictingPlacement: any,
    targetSlotId: string,
  ) => {
    setRoomConflictRequest({
      requestedRoomId,
      targetCourseId,
      targetSlotId,
      conflictingPlacement,
      conflictingCourseId: conflictingPlacement?.course?.id,
      conflictingSlotsIds: [...(conflictingPlacement?.slotsIds || [])],
    });
  };

  const closeRoomConflictModal = () => {
    setRoomConflictRequest(null);
  };

  const handleForceClassroomAssignment = () => {
    if (!roomConflictRequest) return;

    const targetExists = fields.some(
      (placement: any) =>
        placement.course.id === roomConflictRequest.targetCourseId &&
        placement.slotsIds.includes(roomConflictRequest.targetSlotId),
    );
    const conflictExists = fields.some(
      (placement: any) =>
        placement.course.id === roomConflictRequest.conflictingCourseId &&
        placement.slotsIds.length === roomConflictRequest.conflictingSlotsIds.length &&
        roomConflictRequest.conflictingSlotsIds.every((slotId) =>
          placement.slotsIds.includes(slotId),
        ),
    );

    if (!targetExists || !conflictExists) {
      closeRoomConflictModal();
      toast.error(
        "Les cours concernés ont changé. Vérifiez leurs salles avant de réessayer.",
      );
      return;
    }

    replace(
      fields.map((placement: any) => {
        if (
          placement.course.id === roomConflictRequest.targetCourseId &&
          placement.slotsIds.includes(roomConflictRequest.targetSlotId)
        ) {
          return { ...placement, classroomId: roomConflictRequest.requestedRoomId };
        }
        if (
          placement.course.id === roomConflictRequest.conflictingCourseId &&
          placement.slotsIds.length === roomConflictRequest.conflictingSlotsIds.length &&
          roomConflictRequest.conflictingSlotsIds.every((slotId) =>
            placement.slotsIds.includes(slotId),
          )
        ) {
          return { ...placement, classroomId: undefined };
        }
        return placement;
      }),
    );
    closeRoomConflictModal();
    toast.success(
      "Salle attribuée. Le cours qui l'occupait est maintenant sans salle : attribuez-lui une autre salle avant l'enregistrement.",
    );
  };

  // vérifier la disponibilité du professeur
  const checkTeacherAvailability = (
    course: CoursTable,
    seanceStart: number,
    dayId: string,
    ignoredSlotIds: string[] = [],
  ): { isOccupied: boolean; existConflict: any } => {
    const teacherId = course.TeacherProfil?.id;
    // console.log("inside function");
    if (!teacherId) return { isOccupied: false, existConflict: undefined };

    const teacherCourses = allCourses.filter(
      (c: any) => c.TeacherProfil?.id === teacherId,
    );
    const ignoredSlots = new Set(ignoredSlotIds);

    const slotsToCheck = slots.filter(
      (s) =>
        s.dayId === dayId &&
        s.seanceIndex >= seanceStart &&
        s.seanceIndex < seanceStart + (course.seance_num || 0) &&
        s.courseId !== "" &&
        s.courseId !== undefined &&
        !ignoredSlots.has(s.id),
    );

    let existConflict = undefined;

    const isTeacherOccupied = slotsToCheck.some((slot: any) => {
      if (!slot.courseId) return false;
      // Trouver si  le cours déjà placé dans ce slot est fait par ce prof
      existConflict = teacherCourses.find(
        (cours) => cours.id === slot.courseId,
      );
      return existConflict !== undefined;
    });

    return { isOccupied: isTeacherOccupied, existConflict };
  };

  // gérer le drop d'un cours
  const handleDrop = (
    e: React.DragEvent,
    classId: string,
    dayId: string,
    seanceIndex: number,
  ) => {
    try {
      e.preventDefault();
      const id = e.dataTransfer.getData("courseId");

      let newCourse = allCourses.find(
        (course) => course.id === id,
      ) as CoursTable;
      // si un cours est déplacé
      let oldOccurence: any = null;
      if (!newCourse && draggedOccurrence) {
        newCourse = { ...draggedOccurrence.course, seance_num: draggedOccurrence.seance_num }; // conserver la durée propre à ce placement
        oldOccurence = draggedOccurrence;
      }
      if (!newCourse) return;

      const sourceOccupiedSlotIds = oldOccurence
        ? getOccupiedSlotIdsForPlacement(oldOccurence.placement)
        : [];
      const sourceOccupiedSlotIdSet = new Set(sourceOccupiedSlotIds);

      // vérifier si la classe est compatiblle avec le cours dropé
      if (!checkClasseCompatibility(newCourse, classId)) {
        throw new Error("Liaison impossible avec cette classe");
      }

      // vérifier si le professeur n'a pas déja une séances programée à ce moment
      const checkAvailability = checkTeacherAvailability(
        newCourse,
        seanceIndex,
        dayId,
        sourceOccupiedSlotIds,
      );
      // vérifier la disponibilité du professeur
      if (checkAvailability.isOccupied) {
        throw new Error(
          `Conflit: l'enseignant ${newCourse.TeacherProfil.firstname} ${newCourse.TeacherProfil.lastname} est déjà occupé à ce moment en ${checkAvailability.existConflict?.Subject?.label}.`,
        );
      }
      // Un déplacement conserve la salle de cette occurrence. Un nouveau placement
      // utilise la salle par défaut du cours.
      let defaultClassromAvailaible = false;
      const classroomIdToKeep = oldOccurence
        ? oldOccurence.placement?.classroomId
        : newCourse.id_classroom;

      if (classroomIdToKeep) {
        const { isOccupied, existConflict } = checkClassroomAvailability(
          slots,
          fields,
          { id: dayId },
          newCourse,
          classroomIdToKeep,
          seanceIndex,
          dayId,
          oldOccurence?.slotsIds,
        );

        if (isOccupied) {
          if (oldOccurence) {
            const roomLabel = allClassrooms?.find(
              (room) => room.id === classroomIdToKeep,
            )?.label;
            const conflictingSubject =
              existConflict?.course?.Subject?.label || "un autre cours";
            throw new Error(
              `Déplacement impossible : la salle ${roomLabel || "attribuée"} est déjà occupée par « ${conflictingSubject} » à ce créneau. Choisissez une autre salle avant de déplacer le cours.`,
            );
          }
          toast.info(
            `La salle par défaut est déjà occupée par le cours « ${
              existConflict?.course?.Subject?.label || "un autre cours"
            } ». Le placement restera sans salle.`,
          );
        } else {
          defaultClassromAvailaible = true;
        }
      }

      // on vérifie si le jour existe
      const day = days.find((day) => day.id === dayId);
      if (!day) {
        throw new Error("Le jour n'existe pas");
      }

      // Vérifier toutes les classes avant de modifier le planning.
      const targetOccupiedSlotIds = new Set<string>();
      const indexSlots_Id: string[] = [];
      for (const classId of newCourse.id_classes) {
        const classe = allClass.find((c) => c.id === classId);
        if (!classe) continue;

        const slotsToOccupy = slots.filter(
          (s) => s.classeId === classe.id && s.dayId === dayId,
        );

        if (seanceIndex + (newCourse.seance_num || 0) > slotsToOccupy.length) {
          throw new Error(
            `Pas assez de créneaux disponibles pour la classe ${classe.name}`,
          );
        }

        for (
          let index = seanceIndex;
          index < seanceIndex + (newCourse.seance_num || 0);
          index++
        ) {
          const element = slotsToOccupy[index];
          if (element.courseId && !sourceOccupiedSlotIdSet.has(element.id)) {
            throw new Error(
              `Conflit détecté : la séance ${index + 1} du ${
                day.name
              } est déjà occupée dans la classe ${classe.name}`,
            );
          }
          targetOccupiedSlotIds.add(element.id);
        }
        indexSlots_Id.push(slotsToOccupy[seanceIndex].id);
      }

      const sourcePlacement = oldOccurence?.placement;
      const currentFields = sourcePlacement
        ? fields.filter(
            (placement: any) =>
              !(
                placement.course.id === sourcePlacement.course.id &&
                placement.slotsIds.length === sourcePlacement.slotsIds.length &&
                sourcePlacement.slotsIds.every((sourceSlotId: string) =>
                  placement.slotsIds.includes(sourceSlotId),
                )
              ),
          )
        : fields;
      const nextSlots = slots.map((slot) => {
        if (targetOccupiedSlotIds.has(slot.id)) {
          return { ...slot, courseId: newCourse.id };
        }
        if (sourceOccupiedSlotIdSet.has(slot.id)) {
          return { ...slot, courseId: undefined };
        }
        return slot;
      });

      setSlots(nextSlots);
      replace([
        ...currentFields,
        {
          ...sourcePlacement,
          slotsIds: indexSlots_Id,
          course: newCourse,
          seance_num: oldOccurence?.seance_num ?? newCourse.seance_num,
          status: oldOccurence?.placement?.status ?? CourseStatus.SCHEDULED,
          state: oldOccurence?.placement?.state ?? CourseState.NORMAL,
          type_delay: oldOccurence?.placement?.type_delay,
          reason_cancel: oldOccurence?.placement?.reason_cancel,
          day_course: dayId,
          classroomId: defaultClassromAvailaible
            ? classroomIdToKeep
            : undefined,
        },
      ]);

      // enlever le surlignage
      onDragLeave(e);
    } catch (error: any) {
      toast.error(error.message);
      // enlever le surlignage
      onDragLeave(e);
    }
  };

  // gérer le drag enter d'un cours
  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    // Get the dragged course
    const courseId = e.dataTransfer.getData("courseId");
    let draggedCourse = allCourses.find((course) => course.id === courseId);

    if (!draggedCourse && draggedOccurrence) {
      draggedCourse = { ...draggedOccurrence.course, seance_num: draggedOccurrence.seance_num };
    }

    if (!draggedCourse) return;
    // surligné les séances qui vont être occupées par le cours
    if (draggedCourse) {
      const numSeances = draggedCourse.seance_num || 0;
      const currentSlot = e.currentTarget;
      const slotIndex = parseInt(
        currentSlot.getAttribute("data-seanceIndex") || "0",
      );

      // Only highlight if there's enough room for all sessions
      if (slotIndex + numSeances <= seances.length) {
        // Highlight current and subsequent slots needed
        for (let i = slotIndex; i < slotIndex + numSeances; i++) {
          const slotToHighlight = document.querySelector(
            `[data-seanceindex="${i}"][data-dayid="${currentSlot.getAttribute(
              "data-dayId",
            )}"][data-classeid="${currentSlot.getAttribute("data-classeId")}"]`,
          );
          if (slotToHighlight) {
            (slotToHighlight as HTMLElement).style.background =
              "rgba(49, 223, 118, 0.42)";
          }

          const seanceToHighlight = document.querySelector(
            `.seance[data-seanceindex="${
              i + 1
            }"][data-dayid="${currentSlot.getAttribute("data-dayId")}"]`,
          );
          if (seanceToHighlight) {
            (seanceToHighlight as HTMLElement).style.background =
              "rgba(49, 223, 118, 0.42)";
          }
        }

        // Highlight current classes

        const classId = currentSlot.getAttribute("data-classeId");

        const classElement = document.querySelector(
          `.classe[data-classeid="${classId}"]`,
        );
        if (classElement) {
          (classElement as HTMLElement).style.background =
            "rgba(49, 223, 118, 0.42)";
        }
      }
    }

    (e.currentTarget as HTMLElement).style.background =
      "rgba(49, 223, 118, 0.22)";
  };

  // gérer le drop leave d'un cours
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Get the dragged course
    const courseId = e.dataTransfer.getData("courseId");
    let draggedCourse = allCourses.find((course) => course.id === courseId);

    if (!draggedCourse && draggedOccurrence) {
      draggedCourse = { ...draggedOccurrence.course, seance_num: draggedOccurrence.seance_num };
    }

    if (!draggedCourse) return;
    // Remove highlight when dragging leaves
    const currentSlot = e.currentTarget;
    const slotIndex = parseInt(
      currentSlot.getAttribute("data-seanceIndex") || "0",
    );
    const numSeances = draggedCourse?.seance_num || 0;
    // Only highlight if there's enough room for all sessions
    if (slotIndex + numSeances <= seances.length) {
      // Highlight current and subsequent slots needed
      for (let i = slotIndex; i < slotIndex + numSeances; i++) {
        const slotToHighlight = document.querySelector(
          `[data-seanceindex="${i}"][data-dayid="${currentSlot.getAttribute(
            "data-dayId",
          )}"][data-classeid="${currentSlot.getAttribute("data-classeId")}"]`,
        );
        if (slotToHighlight) {
          (slotToHighlight as HTMLElement).style.background = "white";
        }

        const seanceToHighlight = document.querySelector(
          `.seance[data-seanceindex="${
            i + 1
          }"][data-dayid="${currentSlot.getAttribute("data-dayId")}"]`,
        );
        if (seanceToHighlight) {
          (seanceToHighlight as HTMLElement).style.background =
            "var(--blue-slot-bg)";
        }
      }

      // Highlight current classes

      const classId = currentSlot.getAttribute("data-classeId");

      const classElement = document.querySelector(
        `.classe[data-classeid="${classId}"]`,
      );
      if (classElement) {
        (classElement as HTMLElement).style.background = "var(--blue-slot-bg)";
      }
    }

    (e.currentTarget as HTMLElement).style.background = "white";
  };

  return (
    <>
      {!selectedYear?.statut && <InfosAlertYearDisabled />}

      {!(timetableId && timetableVersion) && (
        <div className="card border-0 ">
          <div className="alert alert-danger border border-danger mb-0 p-1">
            <div className="d-flex align-items-start">
              <div className="me-2">
                <i className="feather-info flex-shrink-0" />
              </div>
              <div className="text-danger w-100">
                <div className="fw-semibold d-flex justify-content-between">
                  Créer un emplois du temps avant de placer des cours
                  <button
                    type="button"
                    className="btn-close p-0"
                    data-bs-dismiss="alert"
                    aria-label="Close"
                  >
                    <i className="fas fa-xmark" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="scheduler-container edt-v2">
        <div className="d-flex align-items-center justify-content-between bg-light border-bottom border-gray  gap-2 shadow-sm w-100">
          <div className="timetable-info bg-light rounded shadow-sm ">
            <div className=" d-flex flex-column align-items-center justify-content-center">
              <div>
                <div className="d-flex gap-2">
                  <h4 className="mb-0">Emploi du temps</h4>{" "}
                  {timetableData?.version && (
                    <div className="version-badge">
                      <span className="badge badge-smooth bg-blue">
                        {timetableData?.version?.version_edt}
                      </span>
                    </div>
                  )}
                </div>
                <small className="text-muted d-flex align-items-center">
                  <strong>Période de validité</strong>: {"  "}
                  {timetableData?.valid_from && timetableData?.valid_until
                    ? `${new Date(timetableData.valid_from).toLocaleDateString(
                        "fr-FR",
                      )} - ${new Date(
                        timetableData.valid_until,
                      ).toLocaleDateString("fr-FR")}`
                    : "Non définie"}
                </small>
                <small className="text-muted d-flex align-items-center">
                  <strong>Nom</strong>: {"  "}
                  {!showNewEdtName ? (
                    <span className="d-flex align-items-center gap-2">
                      {newEdtName}
                      {!(timetableId && timetableVersion) && (
                        <i
                          className="fas fa-edit text-primary"
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowNewEDTName(true)}
                        />
                      )}
                    </span>
                  ) : (
                    <span className="d-flex align-items-center gap-2">
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          const inputValue =
                            (e.target as HTMLElement).querySelector("input")
                              ?.value || "";
                          setNewEdtName(inputValue);
                          setShowNewEDTName(false);
                        }}
                        className="d-flex align-items-center gap-1"
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Entrez un nom"
                          defaultValue={newEdtName}
                        />
                        <button
                          type="submit"
                          className="btn btn-sm btn-primary"
                        >
                          <i className="fas fa-check" />
                        </button>
                      </form>
                    </span>
                  )}
                </small>
                {timetableData?.version?.state_edt === "Publiée" ? (
                  <small className="text-muted">
                    <strong>status</strong>:{" "}
                    <span className="badge badge-soft-success ">Publié</span>
                  </small>
                ) : (
                  <small className="text-muted">
                    <strong>status</strong>:{" "}
                    <span className="badge badge-soft-danger ">Non publié</span>
                  </small>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 ">
            <div className="toolbar-left d-flex align-items-center  bg-light rounded shadow-sm mb-2 gap-2">
              {timetableId && timetableVersion ? (
                <button
                  className="btn btn-success h-100 d-flex flex-column align-items-center justify-content-center"
                  onClick={() => setShowSave(true)}
                >
                  <i className="fas fa-save"></i>
                  <span className="mt-1">Sauvegarder</span>
                </button>
              ) : (
                <button
                  className="btn btn-primary h-100 d-flex flex-column align-items-center justify-content-center hover:bg-primary-900"
                  onClick={() => setShowCreate(true)}
                >
                  <i className="fas fa-plus"></i>
                  <span className="mt-1">Créer EDT</span>
                </button>
              )}
            </div>
            <div className="toolbar-right d-flex align-items-center bg-light rounded shadow-sm mb-2">
              <button
                className="btn btn-warning h-100 d-flex flex-column align-items-center"
                onClick={() => setShowApercuByClass((prev) => !prev)}
              >
                <i className="fas fa-print"></i>
                <span className="mt-1">Imprimer</span>
              </button>
            </div>
          </div>
        </div>

        {/* GRID CONTAINER */}

        <div className="edt-workspace">
        <div className="main-container-custom" style={{
          "--day-count": Math.max(days.length, 1),
          "--session-count": Math.max(seances.length, 1),
          "--class-count": Math.max(allClass.length, 1),
        } as React.CSSProperties}>
          {/* TOP DAYS CONTAINER  */}
          <div className="days-container-row">
            <div className="days">
              {days.map((day) => (
                <div key={day.id} className="day">
                  {day.name}
                </div>
              ))}
            </div>
          </div>

          {/* TOP SEANCES CONTAINER */}
          <div className="seances-container-row">
            {days.map((day) => (
              <div className="seances" key={day.id}>
                {seances.map((seance) => (
                  <div
                    key={seance.id}
                    className="seance"
                    data-seanceindex={seance.index}
                    data-dayid={day.id}
                  >
                    <span>{seance.start_time?.slice(0, 5)}</span>
                    <span className="edt-session-end">{seance.end_time?.slice(0, 5)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* LEFT CLASS CONTAINER */}
          <div className="classe-container-column">
            <div className="classes">
              <div className="classe">
                <ImageWithBasePath
                  src="assets/img/logo_uie.png"
                  alt="img"
                  className="img-fluid"
                  height={100}
                  width={100}
                />
              </div>
              {allClass.map((classe) => (
                <div
                  key={classe.id}
                  className={`classe ${selectedClass?.id === classe.id ? "is-selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selectedClass?.id === classe.id}
                  onKeyDown={event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedClass(classe); setCourseSearch(""); } }}
                  data-classeid={classe.id}
                  onClick={() => { setSelectedClass(classe); setCourseSearch(""); }}
                >
                  {classe.name}
                </div>
              ))}
            </div>
          </div>

          {/* SUBGRID OF OUR MAIN UI */}
          <div className="subgrid-main-ui">
            {allClass.map((classe) =>
              days.map((day) => (
                <div key={classe.id + day.id} className="slot-group">
                  {[...Array(seances?.length || 0)].map((_, index) => {
                    const slotId = classe.id + day.id + index;
                    const placedCourse = fields.find((pc) =>
                      pc.slotsIds.includes(slotId),
                    );
                    const isFirstSlot = placedCourse?.slotsIds.includes(slotId);

                    return (
                      <div
                        key={slotId}
                        className={`slot ${placedCourse ? "occupied" : ""}`}
                        id={slotId}
                        data-dayid={day.id}
                        data-classeid={classe.id}
                        data-seanceindex={index}
                        onDrop={(e) =>
                          handleDrop(e, classe.id, day.id, index)
                        }
                        onDragEnter={onDragEnter}
                        onDragLeave={onDragLeave}
                        onDragOver={(e) => e.preventDefault()}
                      >
                        {isFirstSlot && placedCourse && (
                          <OverlayTrigger
                            trigger={[]}
                            show={openPlacedCoursePopover === slotId}
                            onToggle={(show) =>
                              setOpenPlacedCoursePopover(show ? slotId : null)
                            }
                            placement="top"
                            overlay={
                              <Popover
                                id={`popover-${slotId}`}
                                className="edt-course-popover"
                              >
                                <Popover.Header as="h3">
                                  {placedCourse?.course?.Subject?.label}
                                </Popover.Header>
                                <Popover.Body>
                                  <CourseBubble
                                    allClassrooms={allClassrooms}
                                    placedCourse={placedCourse}
                                    placedCourses={fields}
                                    slotId={slotId}
                                    day={day}
                                    slots={slots}
                                    setSlots={(slots) => setSlots(slots)}
                                    setPlacedCourses={(newCourses) =>
                                      replace(newCourses)
                                    }
                                    seances={seances.filter(
                                      (seance) =>
                                        seance.index >= index + 1 &&
                                        seance.index <=
                                          index +
                                            (placedCourse?.seance_num ?? placedCourse?.course?.seance_num ?? 0),
                                    )}
                                    onSessionCountChange={(count) =>
                                      handlePlacementSessionCountChange(placedCourse, slotId, count)
                                    }
                                    onClassroomConflict={(requestedRoomId, conflict, targetSlotId) =>
                                      handleClassroomConflict(
                                        placedCourse.course.id,
                                        requestedRoomId,
                                        conflict,
                                        targetSlotId,
                                      )
                                    }
                                  />
                                </Popover.Body>
                              </Popover>
                            }
                            rootClose
                          >
                            <div
                              draggable={placedCourse.status !== CourseStatus.CANCELLED}
                              onContextMenu={(event) => {
                                event.preventDefault();
                                setOpenPlacedCoursePopover(slotId);
                              }}
                              onDragStart={(e) => {
                                if (placedCourse.status === CourseStatus.CANCELLED) {
                                  e.preventDefault();
                                  toast.info("Un cours annulé ne peut pas être déplacé.");
                                  return;
                                }
                                setDraggedOccurence({
                                  course: placedCourse?.course as CoursTable,
                                  dayId: day.id,
                                  seanceIndex: index,
                                  seance_num: placedCourse?.seance_num ?? placedCourse?.course?.seance_num ?? 1,
                                  slotsIds: [...(placedCourse?.slotsIds || [])],
                                  placement: placedCourse,
                                });
                                setOpenPlacedCoursePopover(null);

                                // 🎨 Ghost drag
                                const ghost = document.createElement("div");
                                ghost.innerText =
                                  placedCourse?.course?.Subject?.label;
                                ghost.style.padding = "5px 10px";
                                ghost.style.width = "300px";
                                ghost.style.background = "#3498db";
                                ghost.style.color = "white";
                                ghost.style.borderRadius = "5px";
                                document.body.appendChild(ghost);
                                e.dataTransfer.setDragImage(ghost, 0, 0);
                                setTimeout(
                                  () => document.body.removeChild(ghost),
                                  0,
                                );
                              }}
                              onDragEnd={() => setDraggedOccurence(null)}
                              className={`edt-placed-course ${placedCourse.status === CourseStatus.CANCELLED ? "is-cancelled" : ""}`}
                              title={placedCourse.status === CourseStatus.CANCELLED ? "Cours annulé : déplacement désactivé" : undefined}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  setOpenPlacedCoursePopover(slotId);
                                }
                              }}
                              aria-label={`Détails du cours ${placedCourse.course.Subject?.label}`}
                              style={{
                                "--course-color": getColorPair(placedCourse.course.TeacherProfil?.id ?? placedCourse.course.id).dark,
                                backgroundColor: getColorPair(placedCourse.course.TeacherProfil?.id ?? placedCourse.course.id).light,
                                width: `calc(${100 * (placedCourse.seance_num ?? placedCourse.course.seance_num ?? 1)}% + ${2 * ((placedCourse.seance_num ?? placedCourse.course.seance_num ?? 1) - 1)}px)`,
                              } as React.CSSProperties}
                            >
                              <strong className="edt-course-title">{placedCourse.course.Subject?.label}</strong>
                              <span className="edt-course-teacher">{[placedCourse.course.TeacherProfil?.lastname, placedCourse.course.TeacherProfil?.firstname].filter(Boolean).join(" ")}</span>
                              <span className="edt-course-meta">
                                {!placedCourse.classroomId ? <span className="edt-room-missing">Salle à définir</span> :
                                  <span>{allClassrooms?.find(room => room.id === placedCourse.classroomId)?.label || "Salle affectée"}</span>}
                                {placedCourse.status === CourseStatus.CANCELLED && <span className="edt-status-cancelled">Annulé</span>}
                                {placedCourse.state === CourseState.RATTRAPAGE && <span className="edt-status-makeup">Rattrapage</span>}
                              </span>
                            </div>
                          </OverlayTrigger>
                        )}
                      </div>
                    );
                  })}
                </div>
              )),
            )}
          </div>
        </div>
        {selectedClass && <aside className="edt-course-panel" aria-label="Cours de la classe">
            <div className="edt-panel-heading">
              <div><span className="edt-eyebrow">COURS DE LA CLASSE</span><h4>{selectedClass.name}</h4></div>
              <button type="button" className="btn btn-light btn-sm" aria-label="Fermer les cours de la classe" onClick={() => setSelectedClass(undefined)}><i className="ti ti-x" /></button>
            </div>
            <p className="edt-panel-hint">Glissez un cours sur le planning. Cliquez dessus pour le modifier.</p>
            <Button variant="primary" className="w-100 mb-3" onClick={() => setShowCreateCourse(true)}><i className="ti ti-plus me-2" />Créer un cours</Button>
            <input type="search" className="form-control mb-3" placeholder="Matière ou enseignant…" aria-label="Rechercher un cours" value={courseSearch} onChange={event => setCourseSearch(event.target.value)} />
            <div className="edt-course-list">
              {allCourses.filter(course => course.id_classes.includes(selectedClass.id))
                .filter(course => `${course.Subject?.label || ""} ${course.TeacherProfil?.lastname || ""} ${course.TeacherProfil?.firstname || ""}`.toLocaleLowerCase("fr").includes(courseSearch.toLocaleLowerCase("fr")))
                .sort((a, b) => (a.Subject?.label || "").localeCompare(b.Subject?.label || "", "fr"))
                .map(course => <CourseCard key={course.id} course={course} onEdit={() => { setEditCourse(course); setShowEditCourse(true); }} />)}
              <p className="edt-empty-results">Aucun cours à afficher.</p>
            </div>
        </aside>}
        </div>
      </div>
      {/* Modal pour la création d'un emploi du temps */}
      <EDTCreateModal
        show={showCreate}
        onClose={() => setShowCreate(false)}
        seances={seances}
        slots={slots}
        days={days}
        published={timetableData?.version?.state_edt === "Publiée"}
        courses={allCourses}
        placedCourses={fields}
        newEDTName={newEdtName}
        setTimetableId={setTimeTableId}
        setTimetableVersion={setTimetableVersion}
      />
      {/* Modal pour enregistrer un emploi du temps */}
      <EDTSaveModal
        show={showSave}
        onClose={() => setShowSave(false)}
        seances={seances}
        slots={slots}
        days={days}
        classes={allClass}
        published={timetableData?.version?.state_edt === "Publiée"}
        idEDT={timetableData?.id}
        idVersionEDT={timetableData?.version?.id}
        placedCourses={fields}
        timetableData={timetableData}
        setTimetableId={setTimeTableId}
        setTimetableVersion={setTimetableVersion}
      />
      <Modal
        show={Boolean(roomConflictRequest)}
        onHide={closeRoomConflictModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title>Cette salle est déjà occupée</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {roomConflictRequest && (() => {
            const conflict = roomConflictRequest.conflictingPlacement;
            const startSlot = slots.find((slot) =>
              conflict.slotsIds.includes(slot.id),
            );
            const firstSessionIndex = startSlot?.seanceIndex ?? 0;
            const sessionCount = conflict.seance_num ?? conflict.course.seance_num ?? 1;
            const conflictSessions = seances.filter(
              (session) =>
                session.index >= firstSessionIndex + 1 &&
                session.index <= firstSessionIndex + sessionCount,
            );
            const classNames = conflict.slotsIds
              .map((id: string) => slots.find((slot) => slot.id === id)?.classeId)
              .map((id: string) => allClass.find((classe) => classe.id === id)?.name)
              .filter(Boolean);
            const teacher = conflict.course.TeacherProfil;
            const roomLabel = allClassrooms?.find(
              (room) => room.id === roomConflictRequest.requestedRoomId,
            )?.label;

            return (
              <>
                <p className="mb-2">
                  La salle <strong>{roomLabel || "sélectionnée"}</strong> est utilisée par ce cours :
                </p>
                <ul className="mb-3">
                  <li><strong>Matière :</strong> {conflict.course.Subject?.label || "Non définie"}</li>
                  <li>
                    <strong>Enseignant :</strong>{" "}
                    {[teacher?.firstname, teacher?.lastname].filter(Boolean).join(" ") || "Non défini"}
                  </li>
                  <li><strong>Classe(s) :</strong> {classNames.join(", ") || "Non définie"}</li>
                  <li>
                    <strong>Créneau :</strong>{" "}
                    {days.find((day) => day.id === conflict.day_course)?.name || "Jour non défini"}
                    {conflictSessions.length > 0 &&
                      `, ${conflictSessions[0].start_time} - ${conflictSessions[conflictSessions.length - 1].end_time}`}
                  </li>
                </ul>
                <p className="mb-0">
                  Si vous forcez l’attribution, ce cours perdra sa salle dans le brouillon. Vous devrez lui en choisir une autre avant d’enregistrer l’EDT.
                </p>
              </>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="d-flex flex-wrap justify-content-end gap-2">
          <Button
            className="flex-grow-1"
            variant="outline-primary"
            onClick={closeRoomConflictModal}
          >
            Choisir une autre salle
          </Button>
          <Button
            className="flex-grow-1"
            variant="danger"
            onClick={handleForceClassroomAssignment}
          >
            Forcer l’attribution
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Modal pour ajouter un cours */}
      <CreateCoursModal
        show={showCreateCourse}
        onClose={() => setShowCreateCourse(false)}
        onSaved={fetchAllCourses}
      />

      {/* Modal pour modifier un cours */}
      {activeVersionId ? (
        <EDTCourseSnapshotModal
          show={showEditCourse}
          course={editCourse}
          versionId={activeVersionId}
          classes={allClass}
          classrooms={allClassrooms || []}
          onClose={() => setShowEditCourse(false)}
          onSaved={fetchAllCourses}
        />
      ) : (
        <EditCoursModal
          show={showEditCourse}
          onClose={() => setShowEditCourse(false)}
          coursID={editCourse?.id}
          onSaved={fetchAllCourses}
          seanceCanEdit={
            !fields?.some(
              (placedCourse) => placedCourse?.course?.id === editCourse?.id,
            )
          }
        />
      )}
      {/* modal de l'aperçu de l'EDT par classe */}
      <EDTApercuByClass
        show={showApercuByClass}
        onClose={() => setShowApercuByClass(false)}
        metaData={{
          classes: allClass,
          selectedClass,
          id_versionEDT: timetableData?.version?.id,
        }}
      />
    </>
  );
};

export default Scheduler;
